import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWordPressAuth } from '@/components/wordpress-auth-provider'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { User, MapPin, Hash, Folder, RefreshCw, X, ExternalLink, Users, Edit, Save, Upload, Trash2, Eye, Calendar, Plus } from 'lucide-react'

interface InfluencerProfile {
  id: number
  title: string
  status: string
  first_name: string
  last_name: string
  permalink: string
  country: GlossaryLabelPair[]
  country_raw: string
  sm_platforms: GlossaryLabelPair[]
  sm_platforms_raw: string
  category: GlossaryLabelPair[]
  category_raw: string
  description: string
  featured_image: string
  gallery: string[]
  email: string
  social_media: {
    instagram: SocialMediaData
    tiktok: SocialMediaData
    youtube: SocialMediaData
    x: SocialMediaData
    linkedin: SocialMediaData
  }
}

interface CompanyProfile {
  id: number
  title: string
  email: string
  permalink: string
  campaigns?: Campaign[] // Updated to use Campaign interface
}

interface Campaign {
  id: number
  title: string
  company_name: string
  country: GlossaryLabelPair[]
  country_raw: string
  category: GlossaryLabelPair[]
  category_raw: string
  social_media_platforms: GlossaryLabelPair[]
  social_media_platforms_raw: string
  campaign_type: GlossaryLabelPair[]
  campaign_type_raw: string
  featured_image: string
  description: string
  start_date: string
  start_date_timestamp: number
  end_date: string
  end_date_timestamp: number
  contact_email: string
  created_date: string
  status: string
}

interface SocialMediaData {
  url: string
  followers: string
}

interface GlossaryLabelPair {
  value: string
  label: string
}

interface ApiResponse {
  success: boolean
  message: string
  data: InfluencerProfile | null
}

// Helper functions for campaign display
const getSocialMediaIcon = (platform: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL instead of plugin assets URL
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const iconUrl = `${themeUrl}/assets/images/glossary-icons/${platform}.svg`
  
  return (
    <img 
      src={iconUrl} 
      alt={`${platform} icon`}
      className="w-full h-full object-contain"
      onError={(e) => {
        // Fallback to generic icon if SVG not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-full h-full bg-gray-400 rounded-sm flex items-center justify-center text-xs font-bold text-white'
        fallback.textContent = platform.charAt(0).toUpperCase()
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getCountryFlag = (countryValue: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL instead of plugin assets URL
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const flagUrl = `${themeUrl}/assets/images/glossary-icons/${countryValue}.svg`
  
  return (
    <img 
      src={flagUrl} 
      alt={`${countryValue} flag`}
      className="w-4 h-4 rounded-sm"
      onError={(e) => {
        // Fallback to generic icon if flag not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-4 h-4 bg-gray-200 rounded-sm flex items-center justify-center text-xs'
        fallback.textContent = '🌍'
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getCampaignTypeIcon = (campaignType: string) => {
  const config = (window as any).WP_COCKPIT
  // Use theme URL for campaign type icons - same path as other glossary icons
  const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
  const iconUrl = `${themeUrl}/assets/images/glossary-icons/${campaignType}.png`
  
  return (
    <img 
      src={iconUrl} 
      alt={`${campaignType} icon`}
      className="w-4 h-4 object-contain"
      onError={(e) => {
        // Fallback to generic icon if PNG not found
        e.currentTarget.style.display = 'none'
        const fallback = document.createElement('div')
        fallback.className = 'w-4 h-4 bg-blue-100 rounded-sm flex items-center justify-center text-xs font-bold text-blue-600'
        fallback.textContent = campaignType.charAt(0).toUpperCase()
        e.currentTarget.parentElement!.appendChild(fallback)
      }}
    />
  )
}

const getStatusBadgeVariant = (status: string) => {
  if (!status) return 'outline' // Default for undefined/null status
  const normalizedStatus = status.toLowerCase()
  switch (normalizedStatus) {
    case 'publish':
      return 'default' // Green for published
    case 'draft':
      return 'secondary' // Gray for draft
    case 'pending':
      return 'outline' // Orange for pending
    case 'private':
      return 'destructive' // Red for private
    default:
      return 'outline'
  }
}

const capitalizeFirst = (str: string) => {
  if (!str) return 'Draft' // Default for undefined/null status
  if (str === 'publish') return 'Published'
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Company Profile View Component
function CompanyProfileView() {
  const navigate = useNavigate()
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    title: ''
  })

  // Campaign state
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [campaignsError, setCampaignsError] = useState<string | null>(null)

  // Fetch company profile
  const fetchCompanyProfile = async (force = false) => {
    if (force) setRefreshing(true)
    else setLoading(true)
    setError(null)

    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const url = force 
        ? `${config.restApiUrl}kj-cockpit/v1/company-profile?force=1&_=${Date.now()}`
        : `${config.restApiUrl}kj-cockpit/v1/company-profile?_=${Date.now()}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-WP-Nonce': config.restNonce,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.success && data.data) {
        setCompanyProfile(data.data)
        setFormData({
          title: data.data.title
        })
      } else {
        setError(data.message || 'No company profile found')
      }
    } catch (err) {
      console.error('Error fetching company profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch company profile')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Save company profile
  const handleSave = async () => {
    setSaving(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const isDev = config?.devMode || false
      if (isDev) {
        console.log('[Company Save] Form data being sent:', formData)
      }

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/company-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.restNonce,
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (isDev) {
        console.log('[Company Save] Response from server:', data)
      }
      
      if (data.success) {
        await fetchCompanyProfile(true)
        setIsEditing(false)
      } else {
        throw new Error(data.message || 'Failed to save company profile')
      }
    } catch (err) {
      console.error('Error saving company profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to save company profile')
    } finally {
      setSaving(false)
    }
  }

  const handleRefresh = () => {
    fetchCompanyProfile(true)
  }

  // Fetch company campaigns
  const fetchCampaigns = async () => {
    setCampaignsLoading(true)
    setCampaignsError(null)

    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/company-campaigns?_=${Date.now()}`, {
        method: 'GET',
        headers: {
          'X-WP-Nonce': config.restNonce,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      const isDev = config?.devMode || false
      if (isDev) {
        // Debug: Log the first campaign's structure
        if (data.success && data.data && data.data.length > 0) {
          console.log('[DEBUG] First campaign structure:', data.data[0])
          console.log('[DEBUG] First campaign categories:', data.data[0].category)
        }
      }
      
      if (data.success) {
        setCampaigns(data.data || [])
      } else {
        setCampaignsError(data.message || 'Failed to fetch campaigns')
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err)
      setCampaignsError(err instanceof Error ? err.message : 'Failed to fetch campaigns')
    } finally {
      setCampaignsLoading(false)
    }
  }

  const handleCampaignClick = (campaign: Campaign) => {
    navigate(`/influencer-profile/campaigns/${campaign.id}`)
  }

  const handleCreateCampaign = async () => {
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const isDev = config?.devMode || false
      if (isDev) console.log('[Create Campaign] Starting campaign creation...')

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/create-campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.restNonce,
        },
        body: JSON.stringify({
          title: 'New Campaign',
          status: 'draft'
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (isDev) console.log('[Create Campaign] Response:', data)

      if (data.success && data.data) {
        // Navigate to the newly created campaign
        navigate(`/influencer-profile/campaigns/${data.data.id}`)
      } else {
        throw new Error(data.message || 'Failed to create campaign')
      }
    } catch (error) {
      console.error('Error creating campaign:', error)
      setError(error instanceof Error ? error.message : 'Failed to create campaign')
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString
    }
  }

  const formatGlossaryLabels = (items: GlossaryLabelPair[]) => {
    if (!items || items.length === 0) return 'Not specified'
    return items.map(item => item.label).join(', ')
  }

  // Load company profile and campaigns on component mount
  useEffect(() => {
    fetchCompanyProfile()
    fetchCampaigns()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Spinner className="h-8 w-8 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading company profile...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !companyProfile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Company Profile</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Manage your company profile information
                </p>
              </div>
              <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          <Alert>
            <User className="h-4 w-4" />
            <AlertDescription>
              {error || 'No company profile found for your account. Please contact an administrator to set up your company profile.'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Company Profile</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {isEditing ? 'Edit your company profile information' : 'View your company profile information'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setIsEditing(!isEditing)} 
                variant={isEditing ? "outline" : "outline"}
              >
                <Edit className="h-4 w-4 mr-2" />
                {isEditing ? 'Cancel' : 'Edit Company'}
              </Button>
              {isEditing && (
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  variant="default"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
              <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Company Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Company Information
              </CardTitle>
              <CardDescription>
                Manage your company profile details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Company Name */}
              <div className="grid gap-2">
                <Label htmlFor="companyName">Company Name</Label>
                {isEditing ? (
                  <Input
                    id="companyName"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter company name"
                    className="text-lg"
                  />
                ) : (
                  <div className="text-lg font-medium py-2">{companyProfile.title}</div>
                )}
              </div>

              {/* Email (read-only) */}
              <div className="grid gap-2">
                <Label>Email Address</Label>
                <div className="text-sm text-gray-600 dark:text-gray-400 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                  {companyProfile.email}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Folder className="h-5 w-5" />
                Campaign Management
              </CardTitle>
              <CardDescription>
                View and manage your marketing campaigns and collaborations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaignsLoading ? (
                <div className="text-center py-8">
                  <Spinner className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-gray-600 dark:text-gray-400">Loading campaigns...</p>
                </div>
              ) : campaignsError ? (
                <Alert>
                  <X className="h-4 w-4" />
                  <AlertDescription>
                    {campaignsError}
                  </AlertDescription>
                </Alert>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Folder className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <h3 className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-300">No Campaigns Yet</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                    You don't have any campaigns associated with your company yet. Campaigns will appear here once they are created and linked to your company profile.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Your Campaigns ({campaigns.length})</h3>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleCreateCampaign} 
                        variant="default" 
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Campaign
                      </Button>
                      <Button 
                        onClick={() => fetchCampaigns()} 
                        variant="outline" 
                        size="sm"
                        disabled={campaignsLoading}
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${campaignsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {campaigns.map((campaign) => (
                      <Card 
                        key={campaign.id} 
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleCampaignClick(campaign)}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                {campaign.title}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {campaign.company_name}
                              </p>
                            </div>
                            <Badge 
                              variant={getStatusBadgeVariant(campaign.status)}
                              className="ml-4"
                            >
                              {capitalizeFirst(campaign.status)}
                            </Badge>
                          </div>

                          <div className="space-y-3">
                            {/* Country and Campaign Type Row */}
                            <div className="flex items-center gap-6">
                              <div className="flex items-center gap-2">
                                {campaign.country.length > 0 && getCountryFlag(campaign.country[0].value)}
                                <span className="text-sm font-medium">
                                  {formatGlossaryLabels(campaign.country)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {campaign.campaign_type.length > 0 && getCampaignTypeIcon(campaign.campaign_type[0].value)}
                                <span className="text-sm font-medium">
                                  {formatGlossaryLabels(campaign.campaign_type)}
                                </span>
                              </div>
                            </div>

                            {/* Categories */}
                            {campaign.category.length > 0 && (
                              <div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 mb-2 block">Categories:</span>
                                <div className="flex flex-wrap gap-1">
                                  {campaign.category.map((cat, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {cat.label}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Social Media Platforms */}
                            {campaign.social_media_platforms.length > 0 && (
                              <div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 mb-2 block">Social Platforms:</span>
                                <div className="flex flex-wrap gap-2">
                                  {campaign.social_media_platforms.map((platform, index) => (
                                    <Badge key={index} variant="outline" className="text-xs flex items-center gap-1">
                                      <div className="w-3 h-3">
                                        {getSocialMediaIcon(platform.value)}
                                      </div>
                                      {platform.label}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Timeline */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>{formatDate(campaign.start_date)}</span>
                                </div>
                                {campaign.end_date && (
                                  <div className="flex items-center gap-1">
                                    <span>→</span>
                                    <span>{formatDate(campaign.end_date)}</span>
                                  </div>
                                )}
                              </div>
                              <Button size="sm" variant="ghost" className="text-xs h-8">
                                View Details
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default function InfluencerProfilePage() {
  const { user } = useWordPressAuth()
  
  // Check user roles to determine what to show
  const userRoles = (window as any).WP_COCKPIT?.roles || []
  const isCompany = userRoles.includes('company')
  const isInfluencer = userRoles.includes('influencer')
  const isAdmin = userRoles.includes('administrator')
  
  // Show company view for company users (unless they're also influencers or admins)
  if (isCompany && !isInfluencer && !isAdmin) {
    return <CompanyProfileView />
  }
  
  // For influencers, admins, or users with multiple roles, show the existing influencer view
  const [profile, setProfile] = useState<InfluencerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    description: '',
    country: '',
    category: [] as string[], // Changed to array for multiselect
    sm_platforms: [] as string[],
    featured_image: '',
    gallery: [] as string[],
    status: 'draft', // Add status field
    social_media: {
      instagram: { url: '', followers: '' },
      tiktok: { url: '', followers: '' },
      youtube: { url: '', followers: '' },
      x: { url: '', followers: '' },
      linkedin: { url: '', followers: '' }
    }
  })
  const [glossaryOptions, setGlossaryOptions] = useState({
    country: [] as Array<{value: string, label: string}>,
    category: [] as Array<{value: string, label: string}>,
    sm_platforms: [] as Array<{value: string, label: string}>
  })

  const fetchInfluencerProfile = async (force = false) => {
    if (force) setRefreshing(true)
    else setLoading(true)
    setError(null)

    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      // Add force parameter to URL if needed
      const url = force 
        ? `${config.restApiUrl}kj-cockpit/v1/influencer-profile?force=1&_=${Date.now()}`
        : `${config.restApiUrl}kj-cockpit/v1/influencer-profile?_=${Date.now()}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-WP-Nonce': config.restNonce,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: ApiResponse = await response.json()
      
      if (data.success && data.data) {
        setProfile(data.data)
      } else {
        setError(data.message || 'No influencer profile found')
      }
    } catch (err) {
      console.error('Error fetching influencer profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch influencer profile')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Load glossary options for selectors
  const loadGlossaryOptions = async () => {
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        console.error('WordPress config not available:', config)
        return
      }

      const isDev = config?.devMode || false
      if (isDev) console.log('Loading glossary options...')

      const [countryRes, categoryRes, smPlatformsRes] = await Promise.all([
        // REST route expects the glossary ID as a path param: /jetengine-glossary/{id}
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/1`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        }),
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/4`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        }),
        fetch(`${config.restApiUrl}kj-cockpit/v1/jetengine-glossary/3`, {
          headers: { 'X-WP-Nonce': config.restNonce }
        })
      ])

      if (isDev) console.log('Glossary responses:', { 
        countryStatus: countryRes.status, 
        categoryStatus: categoryRes.status, 
        smPlatformsStatus: smPlatformsRes.status 
      })

      const [countryData, categoryData, smPlatformsData] = await Promise.all([
        countryRes.json(),
        categoryRes.json(),
        smPlatformsRes.json()
      ])

      if (isDev) console.log('Glossary data:', { countryData, categoryData, smPlatformsData })

      const processGlossaryData = (data: any, name: string) => {
        if (isDev) console.log(`Processing ${name} glossary:`, data)
        if (!data.success) {
          if (isDev) console.error(`${name} glossary failed:`, data.message)
          return []
        }
        
        // Handle new API structure with items array
        if (data.items && Array.isArray(data.items)) {
          const entries = data.items.map((item: any) => ({
            value: item.value || item.item_value,
            label: item.label || item.item_label || item.value || item.item_value
          }))
          if (isDev) console.log(`${name} glossary processed entries from items:`, entries)
          return entries
        }
        
        // Handle old API structure with data object (fallback)
        if (data.data && typeof data.data === 'object') {
          const entries = Object.entries(data.data).map(([value, label]) => ({ value, label: String(label) }))
          if (isDev) console.log(`${name} glossary processed entries from data:`, entries)
          return entries
        }
        
        if (isDev) console.warn(`${name} glossary has no items or data`)
        return []
      }

      setGlossaryOptions({
        country: processGlossaryData(countryData, 'Country'),
        category: processGlossaryData(categoryData, 'Category'),
        sm_platforms: processGlossaryData(smPlatformsData, 'SM Platforms')
      })
    } catch (error) {
      console.error('Error loading glossary options:', error)
    }
  }

  // Populate form data when profile loads
  const populateFormData = (profileData: InfluencerProfile) => {
    const config = (window as any).WP_COCKPIT
    const isDev = config?.devMode || false
    
    const names = profileData.title.split(' ')
    const categoryValues = profileData.category.map(c => c.value)
    const smPlatformValues = profileData.sm_platforms.map(p => p.value)
    
    if (isDev) {
      console.log('[PopulateForm] Profile category data:', profileData.category)
      console.log('[PopulateForm] Category values to set:', categoryValues)
      console.log('[PopulateForm] SM platform values to set:', smPlatformValues)
    }
    
    setFormData({
      first_name: names[0] || '',
      last_name: names.slice(1).join(' ') || '',
      description: profileData.description || '',
      country: profileData.country[0]?.value || '',
      category: categoryValues, // Support multiple selections
      sm_platforms: smPlatformValues,
      featured_image: profileData.featured_image || '',
      gallery: profileData.gallery || [],
      status: profileData.status || 'draft', // Add status field
      social_media: {
        instagram: { 
          url: profileData.social_media.instagram?.url || '', 
          followers: profileData.social_media.instagram?.followers ? String(profileData.social_media.instagram.followers) : '' 
        },
        tiktok: { 
          url: profileData.social_media.tiktok?.url || '', 
          followers: profileData.social_media.tiktok?.followers ? String(profileData.social_media.tiktok.followers) : '' 
        },
        youtube: { 
          url: profileData.social_media.youtube?.url || '', 
          followers: profileData.social_media.youtube?.followers ? String(profileData.social_media.youtube.followers) : '' 
        },
        x: { 
          url: profileData.social_media.x?.url || '', 
          followers: profileData.social_media.x?.followers ? String(profileData.social_media.x.followers) : '' 
        },
        linkedin: { 
          url: profileData.social_media.linkedin?.url || '', 
          followers: profileData.social_media.linkedin?.followers ? String(profileData.social_media.linkedin.followers) : '' 
        }
      }
    })
  }

  useEffect(() => {
    if (user) {
      fetchInfluencerProfile()
      loadGlossaryOptions()
    }
  }, [user])

  useEffect(() => {
    if (profile) {
      populateFormData(profile)
    }
  }, [profile])

  const renderGlossaryItems = (items: GlossaryLabelPair[], fallbackText = 'Not specified') => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return <Badge variant="secondary">{fallbackText}</Badge>
    }
    
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <Badge key={index} variant="secondary">
            {item.label || item.value}
          </Badge>
        ))}
      </div>
    )
  }

  const formatFollowerCount = (count: string): string => {
    if (!count) return '0'
    const num = parseInt(count.replace(/,/g, ''))
    if (isNaN(num)) return count
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toLocaleString()
  }

  const getSocialMediaIcon = (platform: string) => {
    const config = (window as any).WP_COCKPIT
    // Use theme URL instead of plugin assets URL
    const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
    const iconUrl = `${themeUrl}/assets/images/glossary-icons/${platform}.svg`
    
    return (
      <img 
        src={iconUrl} 
        alt={`${platform} icon`}
        className="w-full h-full object-contain"
        onError={(e) => {
          // Fallback to generic icon if SVG not found
          e.currentTarget.style.display = 'none'
          const fallback = document.createElement('div')
          fallback.className = 'w-full h-full bg-gray-400 rounded-sm flex items-center justify-center text-xs font-bold text-white'
          fallback.textContent = platform.charAt(0).toUpperCase()
          e.currentTarget.parentElement!.appendChild(fallback)
        }}
      />
    )
  }

  const getCountryFlag = (countryValue: string) => {
    const config = (window as any).WP_COCKPIT
    // Use theme URL instead of plugin assets URL
    const themeUrl = config?.themeUrl || 'https://influencer.kainjoo.com/wp-content/themes/kj-influencer'
    const flagUrl = `${themeUrl}/assets/images/glossary-icons/${countryValue}.svg`
    
    return (
      <img 
        src={flagUrl} 
        alt={`${countryValue} flag`}
        className="w-4 h-4 rounded-sm"
        onError={(e) => {
          // Fallback to generic icon if flag not found
          e.currentTarget.style.display = 'none'
          const fallback = document.createElement('div')
          fallback.className = 'w-4 h-4 bg-gray-200 rounded-sm flex items-center justify-center text-xs'
          fallback.textContent = '🌍'
          e.currentTarget.parentElement!.appendChild(fallback)
        }}
      />
    )
  }

  const renderSocialMediaSection = (platform: string, data: SocialMediaData, platformLabel: string) => {
    if (!isEditing && !data.url && !data.followers) return null
    
    // Map platform values to formData keys
    const platformMap: {[key: string]: keyof typeof formData.social_media} = {
      'ig': 'instagram',
      'instagram': 'instagram',
      'tiktok': 'tiktok',
      'tt': 'tiktok',
      'youtube': 'youtube',
      'yt': 'youtube',
      'x': 'x',
      'twitter': 'x',
      'linkedin': 'linkedin',
      'li': 'linkedin'
    }
    
    const formPlatformKey = platformMap[platform.toLowerCase()] || platform as keyof typeof formData.social_media
    
    return (
      <div key={platform} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <div className="w-8 h-8">
                  {getSocialMediaIcon(platform)}
                </div>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">{platformLabel}</h4>
            </div>
            
            <div className="grid gap-3">
              <div>
                <Label htmlFor={`${platform}_url`}>Profile URL</Label>
                <Input
                  id={`${platform}_url`}
                  value={formData.social_media[formPlatformKey]?.url || ''}
                  onChange={(e) => handleSocialMediaChange(formPlatformKey, 'url', e.target.value)}
                  placeholder={`Enter ${platformLabel} profile URL`}
                  className={fieldErrors[`social_${formPlatformKey}_url`] ? 'border-red-500' : ''}
                />
                {fieldErrors[`social_${formPlatformKey}_url`] && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors[`social_${formPlatformKey}_url`]}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor={`${platform}_followers`}>Follower Count</Label>
                <Input
                  id={`${platform}_followers`}
                  value={formData.social_media[formPlatformKey]?.followers || ''}
                  onChange={(e) => handleSocialMediaChange(formPlatformKey, 'followers', e.target.value)}
                  placeholder="Enter follower count"
                  type="number"
                  className={fieldErrors[`social_${formPlatformKey}_followers`] ? 'border-red-500' : ''}
                />
                {fieldErrors[`social_${formPlatformKey}_followers`] && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors[`social_${formPlatformKey}_followers`]}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <div className="w-8 h-8">
                  {getSocialMediaIcon(platform)}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">{platformLabel}</h4>
                {data.followers && (
                  <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                    <Users className="h-3 w-3" />
                    {formatFollowerCount(data.followers)} followers
                  </div>
                )}
              </div>
            </div>
            {data.url && (
              <a
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Visit Profile
              </a>
            )}
          </div>
        )}
      </div>
    )
  }

  const handleRefresh = () => {
    fetchInfluencerProfile(true)
  }

  const openImagePopup = (imageUrl: string) => {
    setSelectedImage(imageUrl)
  }

  const closeImagePopup = () => {
    setSelectedImage(null)
  }

  // Close popup on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeImagePopup()
      }
    }

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden' // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedImage])

  // Validate required fields for publishing
  const validateForPublish = () => {
    const errors: Record<string, string> = {}
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required'
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required'
    }
    
    if (!formData.country) {
      errors.country = 'Country is required'
    }
    
    if (formData.category.length === 0) {
      errors.category = 'At least one category is required'
    }
    
    if (formData.sm_platforms.length === 0) {
      errors.sm_platforms = 'At least one social media platform is required'
    }
    
    // Validate social media presence fields for selected platforms
    for (const platformValue of formData.sm_platforms) {
      // Map platform values to our social media data structure (same mapping as in renderSocialMediaSection)
      let socialKey: keyof typeof formData.social_media
      let platformLabel: string
      
      switch (platformValue.toLowerCase()) {
        case 'ig':
        case 'instagram':
          socialKey = 'instagram'
          platformLabel = 'Instagram'
          break
        case 'tiktok':
        case 'tt':
          socialKey = 'tiktok'
          platformLabel = 'TikTok'
          break
        case 'youtube':
        case 'yt':
          socialKey = 'youtube'
          platformLabel = 'YouTube'
          break
        case 'x':
        case 'twitter':
          socialKey = 'x'
          platformLabel = 'X (Twitter)'
          break
        case 'linkedin':
        case 'li':
          socialKey = 'linkedin'
          platformLabel = 'LinkedIn'
          break
        default:
          continue // Skip unknown platforms
      }
      
      const socialData = formData.social_media[socialKey]
      if (!socialData?.url?.trim()) {
        errors[`social_${socialKey}_url`] = `${platformLabel} URL is required`
      }
      if (!socialData?.followers?.trim()) {
        errors[`social_${socialKey}_followers`] = `${platformLabel} followers count is required`
      }
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    }
    
    if (!formData.featured_image) {
      errors.featured_image = 'Featured image is required'
    }
    
    if (formData.gallery.length === 0) {
      errors.gallery = 'At least one gallery image is required'
    }
    
    return errors
  }

  // Form field handlers
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear field error when user starts editing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleMultiSelectChange = (field: string, value: string) => {
    const config = (window as any).WP_COCKPIT
    const isDev = config?.devMode || false
    
    setFormData(prev => {
      const currentValues = prev[field as keyof typeof prev] as string[]
      const isSelected = currentValues.includes(value)
      
      const newValues = isSelected 
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
      
      if (isDev) {
        console.log(`[MultiSelect] Field: ${field}, Value: ${value}, Action: ${isSelected ? 'remove' : 'add'}`)
        console.log(`[MultiSelect] New ${field} values:`, newValues)
      }
      
      return {
        ...prev,
        [field]: newValues
      }
    })
    
    // Clear field error when user interacts with field
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleSocialMediaChange = (platform: string, field: 'url' | 'followers', value: string) => {
    setFormData(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: {
          ...prev.social_media[platform as keyof typeof prev.social_media],
          [field]: value
        }
      }
    }))
    
    // Clear field error when user starts editing social media fields
    const errorKey = `social_${platform}_${field}`
    if (fieldErrors[errorKey]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
  }

  const handleFeaturedImageChange = (imageUrl: string) => {
    setFormData(prev => ({ ...prev, featured_image: imageUrl }))
    
    // Clear featured image error when image is added
    if (fieldErrors.featured_image) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.featured_image
        return newErrors
      })
    }
  }

  const handleGalleryAdd = (imageUrl: string) => {
    if (imageUrl.trim()) {
      setFormData(prev => ({ 
        ...prev, 
        gallery: [...prev.gallery, imageUrl.trim()] 
      }))
      // Clear gallery validation errors when images are added
      setFieldErrors(prev => ({
        ...prev,
        gallery: ''
      }))
    }
  }

  const handleGalleryRemove = (index: number) => {
    setFormData(prev => ({ 
      ...prev, 
      gallery: prev.gallery.filter((_, i) => i !== index) 
    }))
    
    // Clear gallery error when removing images (though this might actually create an error if it becomes empty)
    if (fieldErrors.gallery) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.gallery
        return newErrors
      })
    }
  }

  // Image upload via REST API
  const uploadImage = async (file: File, imageType: 'featured' | 'gallery'): Promise<string> => {
    const config = (window as any).WP_COCKPIT
    if (!config?.restApiUrl || !config?.restNonce) {
      throw new Error('WordPress connection not available')
    }

    const formData = new FormData()
    formData.append('image', file)
    formData.append('image_type', imageType)

    const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/upload-image`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.restNonce,
      },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to upload image')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error(data.message || 'Failed to upload image')
    }

    return data.url
  }

  const handleFeaturedImageUpload = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        setUploading(true)
        const url = await uploadImage(file, 'featured')
        handleFeaturedImageChange(url)
      } catch (error) {
        console.error('Error uploading featured image:', error)
        setError(error instanceof Error ? error.message : 'Failed to upload image')
      } finally {
        setUploading(false)
      }
    }
    input.click()
  }

  const handleGalleryImageUpload = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      if (files.length === 0) return

      try {
        setUploading(true)
        for (const file of files) {
          const url = await uploadImage(file, 'gallery')
          handleGalleryAdd(url)
        }
      } catch (error) {
        console.error('Error uploading gallery images:', error)
        setError(error instanceof Error ? error.message : 'Failed to upload images')
      } finally {
        setUploading(false)
      }
    }
    input.click()
  }

  const handleSave = async () => {
    // Clear previous errors
    setFieldErrors({})
    
    // Validate if trying to publish
    if (formData.status === 'publish') {
      const validationErrors = validateForPublish()
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors)
        return
      }
    }
    
    setSaving(true)
    try {
      const config = (window as any).WP_COCKPIT
      if (!config?.restApiUrl || !config?.restNonce) {
        throw new Error('WordPress connection not available')
      }

      const isDev = config?.devMode || false
      if (isDev) {
        console.log('[Save] Form data being sent:', formData)
        console.log('[Save] Category values:', formData.category)
        console.log('[Save] SM Platforms values:', formData.sm_platforms)
      }

      const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/influencer-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.restNonce,
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (isDev) {
        console.log('[Save] Response from server:', data)
      }
      
      if (data.success) {
        // Refresh the profile data to show updated values
        await fetchInfluencerProfile(true)
        setIsEditing(false)
      } else {
        throw new Error(data.message || 'Failed to save profile')
      }
    } catch (err) {
      console.error('Error saving profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Spinner className="h-8 w-8 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading influencer profile...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Influencer Profile</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Manage your influencer profile information
                </p>
              </div>
              <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          <Alert>
            <User className="h-4 w-4" />
            <AlertDescription>
              {error || 'No influencer profile found for your account. Please contact an administrator to set up your influencer profile.'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Influencer Profile</h1>
                {isEditing ? (
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="publish">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge 
                    variant={getStatusBadgeVariant(profile.status)}
                    className="text-sm px-3 py-1"
                  >
                    {capitalizeFirst(profile.status)}
                  </Badge>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {isEditing ? 'Edit your influencer profile information' : 'View your influencer profile information'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setIsEditing(!isEditing)} 
                variant={isEditing ? "outline" : "outline"}
              >
                <Edit className="h-4 w-4 mr-2" />
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </Button>
              {isEditing && (
                <Button 
                  onClick={handleSave} 
                  disabled={saving || uploading}
                  variant="default"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {uploading ? 'Image Uploading...' : saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
              <Button 
                onClick={() => window.open(profile.permalink, '_blank')} 
                variant="outline"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Profile
              </Button>
              <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Main Profile Card */}
          <Card>
            <CardHeader>
              <div className="flex items-start gap-6">
                {isEditing ? (
                  <div className="flex-shrink-0 space-y-3">
                    <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      {formData.featured_image ? (
                        <img 
                          src={formData.featured_image} 
                          alt="Profile Picture" 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Button
                        onClick={handleFeaturedImageUpload}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={uploading}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {formData.featured_image && (
                        <Button
                          onClick={() => handleFeaturedImageChange('')}
                          variant="outline"
                          size="sm"
                          className="w-full text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      )}
                      {fieldErrors.featured_image && (
                        <p className="text-red-500 text-sm">{fieldErrors.featured_image}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  profile.featured_image && (
                    <div className="flex-shrink-0">
                      <img 
                        src={profile.featured_image} 
                        alt="Profile Picture" 
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openImagePopup(profile.featured_image)}
                      />
                    </div>
                  )
                )}
                <div className="flex-1">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first_name">First Name</Label>
                          <Input
                            id="first_name"
                            value={formData.first_name}
                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                            placeholder="Enter first name"
                            className={fieldErrors.first_name ? 'border-red-500' : ''}
                          />
                          {fieldErrors.first_name && (
                            <p className="text-red-500 text-sm mt-1">{fieldErrors.first_name}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="last_name">Last Name</Label>
                          <Input
                            id="last_name"
                            value={formData.last_name}
                            onChange={(e) => handleInputChange('last_name', e.target.value)}
                            placeholder="Enter last name"
                            className={fieldErrors.last_name ? 'border-red-500' : ''}
                          />
                          {fieldErrors.last_name && (
                            <p className="text-red-500 text-sm mt-1">{fieldErrors.last_name}</p>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{profile.email}</p>
                    </div>
                  ) : (
                    <>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <User className="h-6 w-6" />
                        {profile.first_name} {profile.last_name}
                      </CardTitle>
                      <CardDescription className="text-lg mt-1">{profile.email}</CardDescription>
                      {profile.first_name && profile.last_name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          Full Name: {profile.title}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Country and Category Row */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    {isEditing ? (
                      <div>
                        <Label htmlFor="country">Country</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {glossaryOptions.country.map((country) => (
                            <Badge
                              key={country.value}
                              variant={formData.country === country.value ? 'default' : 'outline'}
                              className="cursor-pointer flex items-center gap-1"
                              onClick={() => handleInputChange('country', formData.country === country.value ? '' : country.value)}
                            >
                              {getCountryFlag(country.value)}
                              {country.label}
                            </Badge>
                          ))}
                        </div>
                        {fieldErrors.country && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.country}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Country:</span>
                        <div className="flex items-center gap-2">
                          {profile.country.length > 0 && getCountryFlag(profile.country[0].value)}
                          {renderGlossaryItems(profile.country)}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    {!isEditing && (
                      <div className="flex items-center gap-2">
                        <Folder className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Category:</span>
                        {renderGlossaryItems(profile.category)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Category Selection (Edit Mode) */}
                {isEditing && (
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Select all that apply:</p>
                      <div className="flex flex-wrap gap-2">
                        {glossaryOptions.category.map((option) => {
                          const isSelected = formData.category.includes(option.value)
                          return (
                            <Badge
                              key={option.value}
                              variant={isSelected ? "default" : "outline"}
                              className={`cursor-pointer transition-colors px-4 py-2 text-sm ${
                                isSelected 
                                  ? "bg-primary hover:bg-primary/90 text-primary-foreground" 
                                  : "hover:bg-gray-100 dark:hover:bg-gray-800"
                              }`}
                              onClick={() => handleMultiSelectChange('category', option.value)}
                            >
                              {option.label}
                            </Badge>
                          )
                        })}
                        {glossaryOptions.category.length === 0 && (
                          <span className="text-sm text-gray-500">Loading categories...</span>
                        )}
                      </div>
                      {fieldErrors.category && (
                        <p className="text-red-500 text-sm mt-1">{fieldErrors.category}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Social Media Platforms */}
                <div>
                  {isEditing ? (
                    <div>
                      <Label htmlFor="sm_platforms">Social Media Platforms</Label>
                      <div className="space-y-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Select all that apply:</p>
                        <div className="flex flex-wrap gap-2">
                          {glossaryOptions.sm_platforms.map((option) => {
                            const isSelected = formData.sm_platforms.includes(option.value)
                            return (
                              <Badge
                                key={option.value}
                                variant={isSelected ? "default" : "outline"}
                                className={`cursor-pointer transition-colors px-4 py-2 text-sm flex items-center gap-2 ${
                                  isSelected 
                                    ? "bg-primary hover:bg-primary/90 text-primary-foreground" 
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
                                onClick={() => handleMultiSelectChange('sm_platforms', option.value)}
                              >
                                <div className="w-4 h-4">
                                  {getSocialMediaIcon(option.value)}
                                </div>
                                {option.label}
                              </Badge>
                            )
                          })}
                          {glossaryOptions.sm_platforms.length === 0 && (
                            <span className="text-sm text-gray-500">Loading platforms...</span>
                          )}
                        </div>
                        {fieldErrors.sm_platforms && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.sm_platforms}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">Social Media Platforms:</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        {profile.sm_platforms.map((platform, index) => (
                          <div key={index} className="flex items-center gap-1">
                            <div className="w-4 h-4">
                              {getSocialMediaIcon(platform.value)}
                            </div>
                            <span className="text-sm">{platform.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media Platforms */}
          {(isEditing ? formData.sm_platforms.length > 0 : profile.sm_platforms && profile.sm_platforms.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Social Media Presence</CardTitle>
                <CardDescription>
                  {isEditing ? 'Configure your social media profiles' : 'Follow on these platforms'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {isEditing ? (
                    // Edit mode: Show fields for selected platforms only
                    formData.sm_platforms.map((platformValue) => {
                      const platformOption = glossaryOptions.sm_platforms.find(opt => opt.value === platformValue)
                      const platformLabel = platformOption?.label || platformValue
                      
                      // Map platform values to our social media data structure
                      let socialKey: keyof typeof formData.social_media
                      switch (platformValue.toLowerCase()) {
                        case 'ig':
                        case 'instagram':
                          socialKey = 'instagram'
                          break
                        case 'tiktok':
                        case 'tt':
                          socialKey = 'tiktok'
                          break
                        case 'youtube':
                        case 'yt':
                          socialKey = 'youtube'
                          break
                        case 'x':
                        case 'twitter':
                          socialKey = 'x'
                          break
                        case 'linkedin':
                        case 'li':
                          socialKey = 'linkedin'
                          break
                        default:
                          return null // Skip unknown platforms
                      }
                      
                      return renderSocialMediaSection(
                        platformValue, 
                        formData.social_media[socialKey], 
                        platformLabel
                      )
                    }).filter(Boolean)
                  ) : (
                    // View mode: Show existing data
                    profile.sm_platforms.map((platform) => {
                      const platformValue = platform.value.toLowerCase()
                      let socialData = null
                      let platformLabel = platform.label
                      
                      switch (platformValue) {
                        case 'ig':
                        case 'instagram':
                          socialData = profile.social_media.instagram
                          platformLabel = platform.label || 'Instagram'
                          break
                        case 'tiktok':
                        case 'tt':
                          socialData = profile.social_media.tiktok
                          platformLabel = platform.label || 'TikTok'
                          break
                        case 'youtube':
                        case 'yt':
                          socialData = profile.social_media.youtube
                          platformLabel = platform.label || 'YouTube'
                          break
                        case 'x':
                        case 'twitter':
                          socialData = profile.social_media.x
                          platformLabel = platform.label || 'X (Twitter)'
                          break
                        case 'linkedin':
                        case 'li':
                          socialData = profile.social_media.linkedin
                          platformLabel = platform.label || 'LinkedIn'
                          break
                      }
                      
                      return renderSocialMediaSection(platformValue, socialData || { url: '', followers: '' }, platformLabel)
                    })
                  )}
                </div>
                {isEditing && formData.sm_platforms.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Select social media platforms above to configure your profiles
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Description Card */}
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter description..."
                    rows={4}
                    className={`mt-1 ${fieldErrors.description ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.description}</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {profile.description || 'No description provided.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Gallery Card */}
          <Card>
            <CardHeader>
              <CardTitle>Gallery</CardTitle>
              <CardDescription>
                {isEditing ? 'Manage your image gallery' : 'Your uploaded images'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  {/* Upload images button */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleGalleryImageUpload}
                      variant="outline"
                      className="flex-1"
                      disabled={uploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Images'}
                    </Button>
                  </div>
                  
                  {fieldErrors.gallery && (
                    <p className="text-red-500 text-sm">{fieldErrors.gallery}</p>
                  )}
                  
                  {/* Gallery grid with edit controls */}
                  {formData.gallery.length > 0 && (
                    <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                      {formData.gallery.map((imageUrl, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 group">
                          <img 
                            src={imageUrl} 
                            alt={`Gallery image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const isDev = (window as any).WP_COCKPIT?.devMode || false
                              if (isDev) console.error('Failed to load gallery image:', imageUrl)
                              // Show a placeholder or broken image indicator
                              e.currentTarget.style.display = 'none'
                              const placeholder = document.createElement('div')
                              placeholder.className = 'w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500'
                              placeholder.innerHTML = '📷<br>Image<br>Error'
                              placeholder.style.fontSize = '12px'
                              placeholder.style.textAlign = 'center'
                              if (e.currentTarget.parentElement) {
                                e.currentTarget.parentElement.appendChild(placeholder)
                              }
                            }}
                            onLoad={() => {
                              const isDev = (window as any).WP_COCKPIT?.devMode || false
                              if (isDev) console.log('Gallery image loaded successfully:', imageUrl)
                            }}
                          />
                          <div className="absolute inset-0 bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                            <Button
                              onClick={() => handleGalleryRemove(index)}
                              size="sm"
                              variant="destructive"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {formData.gallery.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No images in gallery. Click "Upload Images" to add photos.
                    </div>
                  )}
                </div>
              ) : (
                profile.gallery && Array.isArray(profile.gallery) && profile.gallery.length > 0 ? (
                  <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {profile.gallery.map((imageUrl, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer">
                        <img 
                          src={imageUrl} 
                          alt={`Gallery image ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                          onClick={() => openImagePopup(imageUrl)}
                          onError={(e) => {
                            const isDev = (window as any).WP_COCKPIT?.devMode || false
                            if (isDev) console.error('Failed to load gallery image (view mode):', imageUrl)
                            // Show a placeholder
                            e.currentTarget.style.display = 'none'
                            const placeholder = document.createElement('div')
                            placeholder.className = 'w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 cursor-pointer'
                            placeholder.innerHTML = '📷<br>Image<br>Error'
                            placeholder.style.fontSize = '12px'
                            placeholder.style.textAlign = 'center'
                            if (e.currentTarget.parentElement) {
                              e.currentTarget.parentElement.appendChild(placeholder)
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No images in gallery.
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </div>

        {/* Image Popup Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
            onClick={closeImagePopup}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={closeImagePopup}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
                aria-label="Close image"
              >
                <X className="w-8 h-8" />
              </button>
              <img 
                src={selectedImage} 
                alt="Full size image"
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}